<template>
  <TableRemote
    iconHeader="add"
    :items="displayedDevcies"
    isSearchable
    isCustomIcon
    :headers="tableHeaders"
    :itemActions="actions"
    :title="$t('administration.devices')"
    :pagination="pagination"
    :centerColumns="['action']"
    :sortableColumns="['type','model']"
    :notSortableColumns="['action', 'analysisTypes']"
    hideDefaultFooter
    @iconAction="onClickAction"
    @sendQueryParams="onQueryParams"
  >
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </TableRemote>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import DevicesForm from '@/components/forms/devices/DevicesForm';
import TableRemote from '@/components/shared/content-views/VTableRemote';

export default {
    components: {
        TableRemote,
    },
    data() {
        return {
            devices: [],
            pagination: {},
            queryParams: { perpage: 25, page: 1, search: '' },
            isFetching: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
        ]),
        displayedDevcies() {
            return this.devices.map(({ id, deviceAnalysisKeys, ...rest }) => ({
                ...rest,
                id,
                deviceAnalysisKeys: deviceAnalysisKeys?.map(({ key }) => key).join(' - '),
            }));
        },
        tableHeaders() {
            return [
                'type',
                'model',
                'deviceAnalysisKeys',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            actions.push({
                name: this.$t('forms.labels.edit'),
                icon: 'edit',
                mode: 'edit',
            });

            actions.push({
                name: this.$t('forms.labels.delete'),
                icon: 'delete',
                mode: 'delete',
                iconAndTextColor: '#EF4135',
            });

            return actions;
        },
    },
    mounted() {
        this.getDevices();
    },
    methods: {
        async getDevices(nextPage = 1) {
            if (this.isFetching) {
                return;
            }
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getDevices(this.queryParams);
                this.devices = [...data.deviceTypes];
                this.pagination = data.pagination;
                this.isFetching = false;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }

            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getDevices(this.queryParams);
            this.devices = [...this.devices, ...data.deviceTypes];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('devices.add'),
                component: Vue.component('DevicesForm', DevicesForm),
                onConfirm: requestPayload => this.addDevice(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getDeviceById(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('devices.edit'),
                    component: Vue.component('DevicesForm', DevicesForm),
                    payload: { isEdition: true, device: data },
                    onConfirm: requestPayload => this.editDevice(item, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('devices.delete'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('devices.confirm') },
                    onConfirm: () => this.removeDevice(item),
                    isForm: false,
                });
            }
        },
        async addImage(selectedImage) {
            const formData = new FormData();
            const [image] = selectedImage;
            formData.append('file', image);
            const { data } = await this.$rq.addFile(null, formData);
            return data;
        },
        prepareDescription(description) {
            const propDes = {};
            Object.values(description).forEach(element => {
                propDes[element.lang] = element.description;
            });
            return propDes;
        },
        async addDevice(requestPayload) {
            const { description, ...rest } = requestPayload;

            if (description) {
                rest.description = this.prepareDescription(description);
            }
            if (requestPayload.selectedImage) {
                const image = await this.addImage(requestPayload.selectedImage);
                rest.picture = image.id;
            }
            await this.$rq.addDevice(rest);
            this.$toasted.global.toast_success({
                message: this.$t('msg.deviceCreated'),
            });
            await this.getDevices();
        },
        async editDevice(device, requestPayload) {
            if (requestPayload) {
                const { defaultAnalysisType, description, ...rest } = requestPayload;
                if (device.type === requestPayload.type) {
                    delete rest.type;
                }
                if (device.model === requestPayload.model) {
                    delete rest.model;
                }
                if (description) {
                    rest.description = this.prepareDescription(description);
                }
                if (requestPayload.selectedImage) {
                    rest.picture = requestPayload.selectedImage[0].id;
                } else rest.picture = requestPayload.picture ? requestPayload.picture.id : null;
                await this.$rq.updateDevice(device.id, rest);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.deviceUpdate'),
                });
            }
            await this.getDevices();
        },
        async removeDevice(category) {
            await this.$rq.removeDevice(category.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.deviceDeleted'),
            });
            await this.getDevices();
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                page: 1, // Reset to the first page on new search
            };
            this.getDevices();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getDevices(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
